import React, { useState, useEffect, useCallback, useMemo } from "react";
import { apiGet, apiPost, apiDelete, toApiUrl } from "../../../services/api";
import { useClinicStore } from "../../../store/clinicStore";
import { loadProcedureRegistry, getMasterIdByProcedureType } from "../../../registry/procedureRegistry";
import { careflow360Api } from "../../careflow360/api/careflow360Api";
import type { ChartEvent } from "../../careflow360/chart/types";
import { resolveTxOptionVisual } from "../../careflow360/chart/txOptions";
import { applyChartMutationClient } from "../toothchart/applyChartMutationClient";
import { useUndoRedo } from "../events/useUndoRedo";
import { eventBus } from "../events/eventBus";
import { normalizeToothStatesToIso, normalizeToothRecordToIso, fdiToIso } from "../../../utils/toothNotation";
import { mapProcedureLabelToCanonicalVisual } from "../../../utils/toothVisualMapping";
import type { ToothEvent, Patient, Visit } from "../../../types/domain";

import ToothChart from "../toothchart/ToothChart";
import ProcedureLauncher from "./ProcedureLauncher";
import ProcedureSessionController from "../procedures/ProcedureSessionController";
import ConsultantSelectionModal from "../ConsultantSelectionModal";
import GridGallery, { GalleryImage } from "../../common/apexo/GridGallery";
import ApexoDrawer from "../../common/apexo/ApexoDrawer";
import type { Consultant } from "../../../pages/Doctor";
import "./ClinicalWorkspace.css";

/** Chart history item: ToothEvent shape plus API fields (chart_history returns tooth, symbol, multi_tooth at top level). */
type ChartHistoryItem = ToothEvent & { tooth?: string; symbol?: string; multi_tooth?: string | null };

/** /api/doctor/visit-state/<id> response */
interface VisitStateResponse {
  tooth_states?: Record<string, string>;
  surfaces?: Record<string, string[]>;
  flags?: Record<string, unknown>;
  movements?: Record<string, unknown>;
}

/** Chart history frame from /resume (symbol, tooth, surface, multi_tooth, timestamp) */
interface ChartHistoryFrame {
  symbol?: string;
  tooth?: string;
  surface?: string | null;
  multi_tooth?: string | null;
  timestamp?: string | null;
  [key: string]: unknown;
}

/** /api/doctor/procedures/<id>/resume response */
interface ResumeProcedureResponse {
  session?: { tooth_no?: string | null;[k: string]: unknown };
  history?: ChartHistoryFrame[];
}

/** Diagnosis dropdown item from /api/admin/diagnosis */
interface DiagnosisItem {
  id?: string | number;
  code?: string;
  description?: string;
  is_active?: boolean;
  [key: string]: unknown;
}

interface AttachmentApiItem {
  id: string | number;
  filename?: string;
  content_type?: string;
  file_size_bytes?: number;
  created_at?: string;
  url?: string;
  thumbnail_url?: string;
}

/** Active procedure session state (launcher or resume) */
interface ActiveProcedureState {
  masterId?: string | null;
  tooth?: string | null;
  teeth?: string[];
  surface?: string | null;
  diagnosisCode?: string | null;
  resume?: boolean;
  resumeSessionId?: string | number;
  session?: { tooth_no?: string | null;[k: string]: unknown };
  history?: ChartHistoryFrame[];
}

interface ClinicalWorkspaceProps {
  patient: Patient | null;
  visit: Visit | null;
  isLocked: boolean;
  onOpenProcedure?: (procInfo: { masterId?: string; tooth?: string; surface?: string | null; diagnosisCode?: string | null }) => void;
  onProcedureCompleted?: () => void;
  onHistoryMutated?: () => void;
  sessionConsultant: Consultant | null | undefined;
  setSessionConsultant: (c: Consultant | null | undefined) => void;
}

const ISO_TOOTH_ORDER = [
  "UR8", "UR7", "UR6", "UR5", "UR4", "UR3", "UR2", "UR1",
  "UL1", "UL2", "UL3", "UL4", "UL5", "UL6", "UL7", "UL8",
  "LR8", "LR7", "LR6", "LR5", "LR4", "LR3", "LR2", "LR1",
  "LL1", "LL2", "LL3", "LL4", "LL5", "LL6", "LL7", "LL8",
] as const;
const ISO_TOOTH_ORDER_MAP: Record<string, number> = ISO_TOOTH_ORDER.reduce((acc, id, idx) => {
  acc[id] = idx;
  return acc;
}, {} as Record<string, number>);

function toIsoFromApexoToothId(toothId: number): string {
  const text = String(toothId || "").trim();
  if (/^[1-4][1-8]$/.test(text)) {
    return fdiToIso(text);
  }
  return text;
}

function toToothEventFromApexo(event: ChartEvent, visitId: number): ToothEvent {
  const persistedOrId = event.persistedId ?? Number(event.id);
  return {
    id: Number(persistedOrId || 0),
    encounter_id: visitId,
    tooth_code: toIsoFromApexoToothId(Number(event.toothId)),
    event_type: "procedure",
    payload: {
      symbol: event.label,
      procedure_type: event.label,
      diagnosis: event.label,
      type: event.type,
      domain: event.domain,
      column_id: event.columnId,
      source: "apexo_stream",
    },
    created_at: new Date(event.timestamp || Date.now()).toISOString(),
  };
}

export default function ClinicalWorkspace({ patient, visit, isLocked, onOpenProcedure, onProcedureCompleted, onHistoryMutated, sessionConsultant, setSessionConsultant }: ClinicalWorkspaceProps) {
  const [activeTeeth, setActiveTeeth] = useState<string[]>([]);

  const [chartState, setChartState] = useState({
    states: {} as Record<string, string>,
    surfaces: {} as Record<string, string[]>,
    flags: {} as Record<string, unknown>,
    movements: {} as Record<string, unknown>
  });

  const { canUndo, canRedo, undo, redo } = useUndoRedo(visit?.visit_id);

  const [chartHistory, setChartHistory] = useState<ChartHistoryItem[]>([]);
  const [apexoEvents, setApexoEvents] = useState<ChartEvent[]>([]);
  const [procedureSessions, setProcedureSessions] = useState<Array<{ id?: number; created_at?: string; procedure_type?: string; tooth_no?: string; status?: string }>>([]);

  /* ============================================================
     Active procedure session routing state
  ============================================================ */

  const [activeProcedure, setActiveProcedure] = useState<ActiveProcedureState | null>(null);
  const [pendingProcedure, setPendingProcedure] = useState<{ master_id?: string; targetTooth?: string | null; targetTeeth?: string[]; surface?: string | null; diagnosisCode?: string | null; resumeSessionId?: string | number } | null>(null);
  const [showConsultantModal, setShowConsultantModal] = useState(false);
  
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);

  /* ============================================================
     Load chart state
  ============================================================ */

  const loadData = useCallback(async () => {
    if (!patient || !visit) return;

    try {
      const [stateRes, historyRes, sessionsRes] = await Promise.all([
        apiGet<VisitStateResponse>(`/api/doctor/visit-state/${visit.visit_id}`),
        apiGet<ChartHistoryItem[]>(`/api/doctor/chart-history/${visit.visit_id}`),
        apiGet<Array<{ id?: number; created_at?: string; procedure_type?: string; tooth_no?: string; status?: string }>>(`/api/doctor/procedures/by-visit/${visit.visit_id}`)
      ]);

      // Normalize to ISO keys so tooth chart (UR8, LL1, etc.) can display backend state (may be FDI e.g. 18, 31)
      setChartState({
        states: normalizeToothStatesToIso(stateRes?.tooth_states || {}),
        surfaces: normalizeToothRecordToIso((stateRes?.surfaces || {}) as Record<string, string[]>),
        flags: normalizeToothRecordToIso((stateRes?.flags || {}) as Record<string, unknown>),
        movements: normalizeToothRecordToIso((stateRes?.movements || {}) as Record<string, unknown>)
      });
      if (process.env.NODE_ENV !== "production") {
        if (process.env.NODE_ENV === "development") {
          console.debug("[ClinicalWorkspace] chart-history raw", historyRes);
        }
      }
      setChartHistory(Array.isArray(historyRes) ? historyRes : []);
      setProcedureSessions(Array.isArray(sessionsRes) ? sessionsRes : []);
    } catch (e) {
      console.error("Failed to load chart data", e);
    }
  }, [patient, visit]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refreshApexoStream = useCallback(async () => {
    if (!visit?.visit_id || !patient?.patient_id) {
      setApexoEvents([]);
      return;
    }
    try {
      const bundle = await careflow360Api.fetchChartEvents(visit.visit_id, String(patient.patient_id));
      const currentVisitEvents = (bundle.events || []).filter(
        (evt) => Number(evt.visitId || 0) === Number(visit.visit_id)
      );
      setApexoEvents(currentVisitEvents);
    } catch (e) {
      console.warn("Failed to load apexo stream for doctor chart", e);
      setApexoEvents([]);
    }
  }, [patient?.patient_id, visit?.visit_id]);

  useEffect(() => {
    let disposed = false;
    void refreshApexoStream();
    if (!visit?.visit_id) return () => { disposed = true; };
    const subscription = careflow360Api.subscribeLive(visit.visit_id, {
      onMessage: (msg) => {
        if (disposed) return;
        const eventName = String(msg?.event || "");
        if (!eventName || eventName === "pong" || eventName === "connected") return;
        void refreshApexoStream();
      },
      onStatus: () => {
        // No-op for now.
      },
    });
    return () => {
      disposed = true;
      subscription.close();
    };
  }, [refreshApexoStream, visit?.visit_id]);

  const mapAttachmentToGallery = useCallback((item: AttachmentApiItem): GalleryImage => {
    const createdAt = item.created_at ? new Date(item.created_at) : null;
    const date = createdAt && !Number.isNaN(createdAt.getTime())
      ? createdAt.toISOString().split("T")[0]
      : undefined;
    const title = (item.filename || "Attachment").trim() || "Attachment";
    const resolveUrl = (value?: string) => {
      const text = String(value || "").trim();
      if (!text) return "";
      if (text.startsWith("/api/")) {
        return toApiUrl(text);
      }
      return text;
    };
    const url = resolveUrl(item.url);
    const thumbnailUrl = resolveUrl(item.thumbnail_url) || url;
    return {
      id: String(item.id),
      url,
      thumbnailUrl,
      title,
      date,
    };
  }, []);

  const loadAttachments = useCallback(async () => {
    if (!visit?.visit_id) {
      setGalleryImages([]);
      return;
    }
    try {
      const res = await apiGet<AttachmentApiItem[]>(`/api/doctor/attachments/visit/${visit.visit_id}`);
      const mapped = Array.isArray(res) ? res.map(mapAttachmentToGallery).filter((img) => Boolean(img.url)) : [];
      setGalleryImages(mapped);
    } catch (e) {
      console.error("Failed to load attachments", e);
      setGalleryImages([]);
    }
  }, [mapAttachmentToGallery, visit?.visit_id]);

  useEffect(() => {
    void loadAttachments();
  }, [loadAttachments]);

  useEffect(() => {
    const handler = (payload: unknown) => {
      const event = payload as {
        type?: string;
        encounterId?: number;
        tooth_code?: string;
        payload?: {
          symbol?: string;
          multi_tooth?: string | null;
          prev_states?: Record<string, string | null>;
          next_states?: Record<string, string | null>;
          undo?: boolean;
        };
      };
      if (!event) return;
      if (visit?.visit_id && event.encounterId !== visit.visit_id) return;
      if (event.type !== "procedure") return;
      const prevStates = event.payload?.prev_states || {};
      const nextStates = event.payload?.next_states || {};
      const isUndo = Boolean(event.payload?.undo);
      setChartState((prev) => {
        const next = {
          ...prev,
          states: { ...(prev.states || {}) }
        };
        const states = next.states as Record<string, string>;
        const applyMap = isUndo ? prevStates : nextStates;
        Object.keys(applyMap).forEach((tooth) => {
          const state = applyMap[tooth];
          states[tooth] = state ? state : "Normal";
        });
        return next;
      });
    };
    eventBus.on("TOOTH_EVENT", handler);
    return () => eventBus.off("TOOTH_EVENT", handler);
  }, [visit?.visit_id]);

  // Load chart history for current visit (used for tooth chart markers)
  useEffect(() => {
    if (!visit?.visit_id) {
      setChartHistory([]);
      return;
    }
    apiGet(`/api/doctor/chart-history/${visit.visit_id}`)
      .then((res) => {
        if (process.env.NODE_ENV !== "production") {
          if (process.env.NODE_ENV === "development") {
            console.debug("[ClinicalWorkspace] chart-history raw (by visit)", res);
          }
        }
        setChartHistory(Array.isArray(res) ? (res as ChartHistoryItem[]) : []);
      })
      .catch(() => setChartHistory([]));
  }, [visit?.visit_id]);

  /* ============================================================
     Load diagnosis list for dropdown
  ============================================================ */

  // Diagnosis list removed (now in diagnosis console)


  /* ============================================================
     Tooth selection
  ============================================================ */

  const setSelectedTooth = useClinicStore((s) => s.setSelectedTooth);

  const handleToothClick = (toothId: string) => {
    if (isLocked) return;

    setActiveTeeth(prev => {
      const next = prev.includes(toothId)
        ? prev.filter(t => t !== toothId)
        : [...prev, toothId];
      if (next.length === 0) setSelectedTooth(null);
      return next;
    });
  };

  /* ============================================================
     Launch procedure (canonical masterId)
  ============================================================ */

  const handleStartProcedure = useCallback(
    async (proc: { master_id?: string; targetTooth?: string | null; targetTeeth?: string[]; surface?: string | null; resumeSessionId?: string | number }) => {
      if (!visit?.visit_id || !patient?.patient_id) {
        alert("No visit or patient context.");
        return;
      }

      // 🔁 RESUME MODE
      if (proc.resumeSessionId != null) {
        try {
          const data = await apiGet<ResumeProcedureResponse>(`/api/doctor/procedures/${proc.resumeSessionId}/resume`);
          await loadProcedureRegistry();
          const resolvedMasterId = getMasterIdByProcedureType(String(data.session?.procedure_type ?? "")) ?? null;

          if (data.history && data.history.length > 0) {
            const newChartState = {
              states: { ...chartState.states },
              surfaces: { ...chartState.surfaces },
              flags: { ...chartState.flags },
              movements: { ...chartState.movements }
            };
            data.history.forEach(evt => {
              applyChartMutationClient(newChartState, { ...evt, timestamp: evt.timestamp ?? undefined } as import("../toothchart/applyChartMutationClient").ChartMutationFrame);
            });
            setChartState(newChartState);
          }
          await loadData();

          setActiveProcedure({
            resume: true,
            resumeSessionId: proc.resumeSessionId,
            session: data.session,
            history: data.history,
            masterId: resolvedMasterId,
            tooth: data.session?.tooth_no || null,
            teeth: [],
            surface: null,
            diagnosisCode: null
          });
        } catch (e) {
          console.error("Failed to resume session", e);
          alert("Failed to resume procedure session");
        }
        return;
      }

      const masterId = proc.master_id;
      if (!masterId) {
        alert("Procedure missing master ID.");
        return;
      }

      // 🔁 NEW SESSION: Check Consultant First
      if (sessionConsultant === undefined) {
        setPendingProcedure(proc);
        setShowConsultantModal(true);
        return;
      }

      setActiveProcedure({
        masterId,
        tooth: proc.targetTooth || (activeTeeth.length === 1 ? activeTeeth[0] : null),
        teeth: proc.targetTeeth || activeTeeth,
        surface: proc.surface || null,
        diagnosisCode: null
      });
    },
    [visit?.visit_id, patient?.patient_id, activeTeeth, chartState, sessionConsultant]
  );

  const handleConsultantSelect = (consultant: Consultant | null) => {
    setSessionConsultant(consultant);
    setShowConsultantModal(false);
    if (pendingProcedure) {
      const proc = pendingProcedure;
      setPendingProcedure(null);
      handleStartProcedure(proc);
    }
  };

  /* ============================================================
     Clear active procedure after completion
  ============================================================ */

  const handleProcedureCompleted = useCallback(async () => {
    setActiveProcedure(null);
    await loadData();
    onProcedureCompleted?.();
  }, [loadData, onProcedureCompleted]);

  const activeTooth =
    activeTeeth.length === 1 ? activeTeeth[0] : null;

  /** Single source of truth for PNG chart rendering: apexo event stream (current visit). */
  const toothEventsForChart = useMemo(() => {
    if (visit?.visit_id && apexoEvents.length > 0) {
      return apexoEvents.map((evt) => toToothEventFromApexo(evt, visit.visit_id as number));
    }
    // Fallback to legacy history while apexo stream is unavailable.
    return (chartHistory || []).map((item) => {
      const rawTooth = (item.tooth ?? (item as { tooth_code?: string }).tooth_code) ?? "";
      const toothCode = /^[1-4][1-8]$/.test(rawTooth) ? fdiToIso(rawTooth) : rawTooth;
      return {
        ...item,
        tooth_code: toothCode,
        tooth: toothCode,
        payload: { symbol: item.symbol ?? (item as { payload?: { symbol?: string } }).payload?.symbol, multi_tooth: item.multi_tooth ?? null }
      };
    });
  }, [apexoEvents, chartHistory, visit?.visit_id]);

  const unsupportedLabelsByTooth = useMemo<Record<string, string[]>>(() => {
    const map: Record<string, string[]> = {};
    for (const evt of apexoEvents) {
      const label = String(evt.label || "").trim();
      if (!label) continue;
      const canonical = mapProcedureLabelToCanonicalVisual(label);
      if (canonical !== "normal") continue;
      const normalized = label.toLowerCase();
      if (normalized === "normal" || normalized === "other") continue;
      const toothCode = toIsoFromApexoToothId(Number(evt.toothId));
      if (!toothCode) continue;
      if (!map[toothCode]) map[toothCode] = [];
      if (!map[toothCode].includes(label)) map[toothCode].push(label);
    }
    return map;
  }, [apexoEvents]);

  const unsupportedAuditRows = useMemo(() => {
    return Object.entries(unsupportedLabelsByTooth)
      .filter(([, labels]) => labels.length > 0)
      .sort(([toothA], [toothB]) => {
        const rankA = ISO_TOOTH_ORDER_MAP[toothA] ?? Number.MAX_SAFE_INTEGER;
        const rankB = ISO_TOOTH_ORDER_MAP[toothB] ?? Number.MAX_SAFE_INTEGER;
        if (rankA !== rankB) return rankA - rankB;
        return toothA.localeCompare(toothB);
      })
      .map(([tooth, labels]) => ({
        tooth,
        labels: labels.map((label) => ({
          label,
          ...resolveTxOptionVisual(label),
        })),
      }));
  }, [unsupportedLabelsByTooth]);

  /* OLD WORKING LAYOUT: clinical-workspace-grid, cw-left > cw-chart-container > cw-chart-header, cw-diagnosis-section, cw-chart-content > ToothChart (no ChartBig) */
  return (
    <div className="clinical-workspace-grid cw-grid-single">
      {/* LEFT: tooth chart */}
      <div className="cw-left">
        <div className="cw-chart-container">
          <div className="cw-chart-header">
            <h3>🦷 Dental Chart</h3>
            <div className="cw-chart-header-right">
              {!isLocked && (
                <div className="cw-undo-redo">
                  <button type="button" className="cw-undo-btn" onClick={undo} disabled={!canUndo} title="Undo last action">⬅ Undo</button>
                  <button type="button" className="cw-redo-btn" onClick={redo} disabled={!canRedo} title="Redo">Redo ➡</button>
                </div>
              )}
              {activeTeeth.length > 0 && (
                <span className="active-tooth-indicator">
                  {activeTeeth.length === 1 ? `Tooth #${activeTeeth[0]}` : `${activeTeeth.length} teeth selected`}
                </span>
              )}
              {activeProcedure && (
                <span className="active-procedure-indicator">{activeProcedure.masterId}</span>
              )}
            </div>
          </div>

          <div className="cw-chart-content" data-testid="chart-content">
            <div className="cw-chart-layout">
              <div className="cw-chart-main">
                <ToothChart
                  toothStates={chartState.states}
                  surfacesMap={chartState.surfaces}
                  flagsMap={chartState.flags}
                  movementsMap={chartState.movements}
                  unsupportedLabelsByTooth={unsupportedLabelsByTooth}
                  selectedTeeth={activeTeeth}
                  onToothClick={handleToothClick}
                  readOnly={isLocked}
                  autoFit={true}
                  allowUpscale={false}
                  toothEvents={toothEventsForChart}
                />
              </div>
              <aside className="cw-unmapped-panel" aria-label="Unmapped treatments and diagnoses">
                <div className="cw-unmapped-header">
                  <h4>Unmapped PNG Labels</h4>
                  <span className="cw-unmapped-legend">+N = additional treatments/diagnoses without PNG state.</span>
                </div>
                {unsupportedAuditRows.length === 0 ? (
                  <p className="cw-unmapped-empty">All current Apexo labels are covered by PNG states.</p>
                ) : (
                  <div className="cw-unmapped-list">
                    {unsupportedAuditRows.map((row) => (
                      <div key={row.tooth} className="cw-unmapped-row">
                        <div className="cw-unmapped-tooth">
                          <span>{row.tooth}</span>
                          <span className="cw-unmapped-count">+{row.labels.length}</span>
                        </div>
                        <div className="cw-unmapped-labels">
                          {row.labels.map((item) => (
                            <span key={`${row.tooth}-${item.label}`} className="cw-unmapped-chip">
                              <span
                                className="cw-unmapped-icon"
                                style={{ color: item.color }}
                                aria-hidden="true"
                              >
                                {item.icon}
                              </span>
                              <span className="cw-unmapped-text">{item.label}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </aside>
            </div>
          </div>
        </div>
        
        <div className="cw-gallery-wrap">
          <GridGallery 
            images={galleryImages} 
            title="Patient Attachments"
            onUpload={(file) => {
              void (async () => {
                if (!visit?.visit_id) {
                  alert("No active visit selected.");
                  return;
                }
                try {
                  const fileBase64 = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                      const value = String(reader.result || "");
                      const base64 = value.includes(",") ? value.split(",")[1] || "" : value;
                      resolve(base64);
                    };
                    reader.onerror = () => reject(new Error("Failed to read file"));
                    reader.readAsDataURL(file);
                  });
                  const created = await apiPost<AttachmentApiItem>(
                    `/api/doctor/attachments/visit/${visit.visit_id}/upload`,
                    {
                      filename: file.name,
                      content_type: file.type || "application/octet-stream",
                      file_base64: fileBase64,
                    }
                  );
                  const mapped = mapAttachmentToGallery(created);
                  if (!mapped.url) throw new Error("Attachment upload returned no image URL");
                  setGalleryImages((prev) => [mapped, ...prev.filter((img) => img.id !== mapped.id)]);
                } catch (e) {
                  console.error("Failed to upload attachment", e);
                  alert("Failed to upload attachment image.");
                }
              })();
            }}
            onDelete={(id) => {
              void (async () => {
                try {
                  await apiDelete(`/api/doctor/attachments/${encodeURIComponent(String(id))}`);
                  setGalleryImages((prev) => prev.filter((img) => img.id !== id));
                } catch (e) {
                  console.error("Failed to delete attachment", e);
                  alert("Failed to delete attachment image.");
                }
              })();
            }}
          />
        </div>
      </div>

      {/* RIGHT: procedure launcher / session controller mapped to drawer */}
      <ApexoDrawer
        isOpen={activeTeeth.length > 0 || activeProcedure !== null}
        onClose={() => {
          setActiveTeeth([]);
          if (activeProcedure) setActiveProcedure(null);
        }}
        title={activeProcedure ? "Active Procedure" : "Procedures"}
        width="450px"
        hasBackdrop={false}
      >
        {!activeProcedure ? (
          <ProcedureLauncher
            activeTooth={activeTeeth.length === 1 ? activeTeeth[0] ?? null : null}
            activeTeeth={activeTeeth}
            onStart={(opts) => { void handleStartProcedure(opts); }}
            isLocked={isLocked}
            patientId={patient?.patient_id}
          />
        ) : (
          <ProcedureSessionController
            patientId={patient?.patient_id}
            visitId={visit?.visit_id != null ? String(visit.visit_id) : null}
            toothNo={activeProcedure.tooth}
            targetTeeth={activeProcedure.teeth}
            toothStates={chartState.states}
            diagnosisCode={activeProcedure.diagnosisCode}
            masterId={activeProcedure.masterId}
            resumeSession={activeProcedure.resume ? activeProcedure.session : null}
            resumeHistory={activeProcedure.history?.map((f) => ({ ...f, timestamp: f.timestamp ?? undefined })) ?? null}
            isLocked={isLocked}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={undo}
            onRedo={redo}
            onChartMutated={loadData}
            onHistoryMutated={onHistoryMutated}
            onUpdated={async (session?: { id?: unknown; step_no?: number; total_steps?: number;[k: string]: unknown }) => {
              if (session && typeof session.id !== "undefined") {
                setActiveProcedure((prev) => (prev ? { ...prev, session } : null));
              }
              await loadData();
            }}
            onProcedureFinished={handleProcedureCompleted}
            onClearWorkflow={() => setActiveProcedure(null)}
            initialSession={activeProcedure?.session ?? null}
            consultantName={sessionConsultant?.name}
            autoStart={true}
          />
        )}
      </ApexoDrawer>

      <ConsultantSelectionModal
        isOpen={showConsultantModal}
        onSelect={handleConsultantSelect}
        onClose={() => setShowConsultantModal(false)}
      />
    </div>
  );
}
